import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Textarea } from "~/components/ui/textarea";

export interface EscalateTaskDialogProps {
  /** The task being escalated, or null when the dialog is closed. */
  task: { description: string } | null;
  open: boolean;
  /** Mirrors Radix Dialog's onOpenChange; suppressed while submitting. */
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the user confirms, with the (optional, trimmed by the caller)
   * reason. The dialog shows a spinner until this settles; on resolve it closes,
   * on reject it surfaces a toast and stays open so the user can retry. The API
   * call lives in the parent.
   */
  onConfirm: (reason: string) => Promise<void>;
}

/**
 * HAN-79 (V2-E5.2) — confirm dialog before escalating a task to a senior
 * clinician, with an optional free-text reason. Mirrors the acknowledgement
 * modal's submit/loading posture (built on shadcn `<Dialog>`).
 */
export function EscalateTaskDialog({
  task,
  open,
  onOpenChange,
  onConfirm,
}: EscalateTaskDialogProps): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("");

  const handleConfirm = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(reason);
      setReason("");
      onOpenChange(false);
    } catch {
      toast.error("Couldn't escalate this task. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent
        onEscapeKeyDown={(e) => {
          if (submitting) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Escalate task to a senior</DialogTitle>
          <DialogDescription className="text-foreground">
            The most-senior available clinician on this ward will be notified. Add an optional
            reason for the record.
          </DialogDescription>
        </DialogHeader>

        {task ? <p className="text-sm text-muted-foreground">Task: {task.description}</p> : null}

        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          rows={3}
          aria-label="Reason for escalation (optional)"
          placeholder="Reason (optional)"
        />

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting}
            data-testid="escalate-confirm"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Escalating…
              </>
            ) : (
              "Escalate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
